import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ApiIdentityResponse } from "api/services/useApiIdentities";
import { getApiIdentities } from "store/services/ApiIdentities/apiIdentitiesThunks";
import { ListResponse } from "types/api";

interface ApiIdentitiesState {
  data: ListResponse<ApiIdentityResponse>;
  page: number;
  rowsPerPage: number;
  ordering: string;
  search: string;
  loading: boolean;
  error: string | null;
  includeRevoked: boolean;
  /** Tracks the in-flight list request so stale responses are ignored. */
  latestRequestId: string | null;
}

const initialState: ApiIdentitiesState = {
  data: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  page: 0,
  rowsPerPage: 25,
  ordering: "-created_at",
  search: "",
  loading: false,
  error: null,
  includeRevoked: false,
  latestRequestId: null,
};

const apiIdentitiesSlice = createSlice({
  name: "apiIdentities",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
    },
    setOrdering: (state, action: PayloadAction<string>) => {
      state.ordering = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setIncludeRevoked: (state, action: PayloadAction<boolean>) => {
      state.includeRevoked = action.payload;
    },
    handleResetApiIdentities: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApiIdentities.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(getApiIdentities.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) {
          return;
        }
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getApiIdentities.rejected, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) {
          return;
        }
        state.loading = false;
        state.error = action.error.message || "Failed to fetch api identities";
      });
  },
});

export const {
  setPage,
  setRowsPerPage,
  setOrdering,
  setSearch,
  setIncludeRevoked,
  handleResetApiIdentities,
} = apiIdentitiesSlice.actions;

export default apiIdentitiesSlice;
